import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { useFeatureFlags } from '../../features/FeatureFlagsContext';
import JsonTreeViewer from '../../components/detail/JsonTreeViewer';
import {
  asPrettyJSON,
  canonicalRequest,
  parseJSONObject,
  RemoteEntityAutocomplete,
  ValidationSummary,
} from './CanonicalEditorCommon';

const managedFields = [
  { key: 'content', label: 'Content', capability: 'listing.content.update', description: 'Title, description, tags, and language' },
  { key: 'pricing', label: 'Pricing', capability: 'listing.pricing.update', description: 'Listing and variant prices' },
  { key: 'media', label: 'Media', capability: 'listing.media.update', description: 'Mockups, videos, order, and featured media' },
  { key: 'taxonomy', label: 'Taxonomy', capability: 'listing.taxonomy.update', description: 'Sections, categories, and collection mappings' },
  { key: 'availability', label: 'Availability', capability: 'listing.availability.update', description: 'Enabled variants and channel availability' },
  { key: 'state', label: 'Publication state', capability: 'listing.state.update', description: 'Publish, pause, and unpublish state' },
];

function supports(connection, capability) {
  return connection?.capabilities?.[capability] === true;
}

function normalizedManagedFields(value) {
  return Array.isArray(value) ? value.filter((item) => managedFields.some((field) => field.key === item)) : [];
}

function ManagedFieldSelector({ value, onChange, connection, disabled }) {
  const toggle = (key) => {
    onChange(value.includes(key) ? value.filter((item) => item !== key) : [...value, key]);
  };
  return (
    <FormGroup>
      {managedFields.map((field) => {
        const unsupported = connection && !supports(connection, field.capability);
        return (
          <FormControlLabel
            key={field.key}
            disabled={disabled || unsupported}
            control={<Checkbox checked={value.includes(field.key)} onChange={() => toggle(field.key)} />}
            label={(
              <Box>
                <Typography variant="body2">{field.label}{unsupported ? ' (unsupported)' : ''}</Typography>
                <Typography variant="caption" color="text.secondary">{field.description}</Typography>
              </Box>
            )}
          />
        );
      })}
    </FormGroup>
  );
}

export function CreatePublicationDialog({ open, listingID, onClose, onCreated }) {
  const { fetchWithAuth } = useAuth();
  const [connection, setConnection] = React.useState(null);
  const [connectionDetail, setConnectionDetail] = React.useState(null);
  const [routeID, setRouteID] = React.useState('');
  const [collections, setCollections] = React.useState([]);
  const [collectionOptions, setCollectionOptions] = React.useState([]);
  const [fields, setFields] = React.useState([]);
  const [overridesText, setOverridesText] = React.useState('{}');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [validation, setValidation] = React.useState(null);

  React.useEffect(() => {
    if (!open) return;
    setConnection(null);
    setConnectionDetail(null);
    setRouteID('');
    setCollections([]);
    setCollectionOptions([]);
    setFields([]);
    setOverridesText('{}');
    setError('');
    setValidation(null);
  }, [open]);

  React.useEffect(() => {
    if (!connection?.id) {
      setConnectionDetail(null);
      setCollectionOptions([]);
      setCollections([]);
      setFields([]);
      setRouteID('');
      return;
    }
    let active = true;
    Promise.all([
      canonicalRequest(fetchWithAuth, `/connections/${connection.id}`),
      canonicalRequest(fetchWithAuth, `/collections?limit=100&connection_id=${connection.id}`),
    ]).then(([connectionPayload, collectionPayload]) => {
      if (!active) return;
      const detail = connectionPayload.item;
      setConnectionDetail(detail);
      setCollectionOptions(Array.isArray(collectionPayload.items) ? collectionPayload.items : []);
      setFields(managedFields.filter((field) => supports(detail, field.capability)).map((field) => field.key));
    }).catch((loadError) => active && setError(loadError.message || 'Unable to load destination options'));
    return () => { active = false; };
  }, [connection?.id, fetchWithAuth]);

  const routes = (connectionDetail?.routes || []).filter((route) => route.sales_connection?.id === connection?.id);

  const create = async () => {
    if (!connection?.id) {
      setError('Choose a sales channel destination.');
      return;
    }
    setLoading(true);
    setError('');
    setValidation(null);
    try {
      const result = await canonicalRequest(fetchWithAuth, '/publications', {
        method: 'POST',
        body: JSON.stringify({
          listing_id: listingID,
          connection_id: connection.id,
          ...(routeID ? { publication_route_id: routeID } : {}),
          managed_field_groups: fields,
          overrides: parseJSONObject(overridesText, 'Channel overrides'),
          collection_ids: collections.map((item) => item.id),
        }),
      });
      onCreated?.(result);
      onClose();
    } catch (createError) {
      setValidation(createError.validation || null);
      setError(createError.message || 'Unable to create publication');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Add a sales channel</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <ValidationSummary validation={validation} />
          <RemoteEntityAutocomplete
            fetchWithAuth={fetchWithAuth}
            endpoint="/connections"
            query={{ kind: 'sales_channel', active: 'true' }}
            label="Sales channel destination"
            value={connection}
            onChange={setConnection}
            getOptionLabel={(option) => `${option.display_name || option.provider} · ${option.provider}`}
            required
          />
          <TextField select label="Publication route" value={routeID} onChange={(event) => setRouteID(event.target.value)} disabled={!connectionDetail} helperText="Optional for an existing remote listing; required by some new-listing workflows.">
            <MenuItem value=""><em>Direct sales-channel route</em></MenuItem>
            {routes.map((route) => <MenuItem key={route.id} value={route.id}>{route.name} · {route.strategy}</MenuItem>)}
          </TextField>
          <Autocomplete
            multiple
            options={collectionOptions}
            value={collections}
            onChange={(event, next) => setCollections(next)}
            getOptionLabel={(option) => option.name || ''}
            isOptionEqualToValue={(option, selected) => option.id === selected.id}
            renderInput={(params) => <TextField {...params} label="Collections" helperText="Only mapped collections are shown for this destination." />}
          />
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2">Fields PODBoss will manage</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Unselected fields remain observed and will not be overwritten.</Typography>
            <ManagedFieldSelector value={fields} onChange={setFields} connection={connectionDetail} />
          </Paper>
          <TextField label="Channel overrides (JSON)" multiline minRows={3} value={overridesText} onChange={(event) => setOverridesText(event.target.value)} helperText="Optional channel-specific values. These do not change the reusable Listing." />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={create} disabled={loading || !connection} startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}>
          {loading ? 'Adding…' : 'Add channel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function ManagePublicationDialog({ open, publication, onClose, onChanged }) {
  const { fetchWithAuth } = useAuth();
  const { flags } = useFeatureFlags();
  const navigate = useNavigate();
  const [detail, setDetail] = React.useState(null);
  const [connectionDetail, setConnectionDetail] = React.useState(null);
  const [fields, setFields] = React.useState([]);
  const [routeID, setRouteID] = React.useState('');
  const [collectionOptions, setCollectionOptions] = React.useState([]);
  const [collections, setCollections] = React.useState([]);
  const [overridesText, setOverridesText] = React.useState('{}');
  const [copyObserved, setCopyObserved] = React.useState(true);
  const [plan, setPlan] = React.useState(null);
  const [workflow, setWorkflow] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [actionLoading, setActionLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [validation, setValidation] = React.useState(null);

  const loadDetail = React.useCallback(async () => {
    if (!publication?.id) return;
    setLoading(true);
    setError('');
    try {
      const payload = await canonicalRequest(fetchWithAuth, `/publications/${publication.id}`);
      const loaded = payload.item;
      const [connectionPayload, collectionPayload] = await Promise.all([
        canonicalRequest(fetchWithAuth, `/connections/${loaded.connection.id}`),
        canonicalRequest(fetchWithAuth, `/collections?limit=100&connection_id=${loaded.connection.id}`),
      ]);
      const options = Array.isArray(collectionPayload.items) ? [...collectionPayload.items] : [];
      (loaded.collections || []).forEach((selected) => {
        if (!options.some((option) => option.id === selected.id)) options.push(selected);
      });
      setDetail(loaded);
      setConnectionDetail(connectionPayload.item);
      setFields(normalizedManagedFields(loaded.managed_field_groups));
      setRouteID(loaded.route?.id || '');
      setCollectionOptions(options);
      setCollections((loaded.collections || []).map((selected) => options.find((option) => option.id === selected.id) || selected));
      setOverridesText(asPrettyJSON(loaded.overrides));
      setPlan(null);
      setWorkflow(null);
      setValidation(null);
    } catch (loadError) {
      setError(loadError.message || 'Unable to load publication');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, publication?.id]);

  React.useEffect(() => {
    if (open) loadDetail();
  }, [loadDetail, open]);

  const routes = (connectionDetail?.routes || []).filter((route) => route.sales_connection?.id === detail?.connection?.id);

  const saveSettings = async () => {
    if (!detail) return;
    setActionLoading(true);
    setError('');
    setValidation(null);
    try {
      const result = await canonicalRequest(fetchWithAuth, `/publications/${detail.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          expected_version: detail.desired_version,
          publication_route_id: routeID || null,
          overrides: parseJSONObject(overridesText, 'Channel overrides'),
          collection_ids: collections.map((item) => item.id),
        }),
      });
      setValidation(result.validation || null);
      await loadDetail();
      onChanged?.();
    } catch (saveError) {
      setValidation(saveError.validation || null);
      setError(saveError.status === 409 ? 'This publication changed. Reload it before saving settings.' : saveError.message || 'Unable to save publication settings');
    } finally {
      setActionLoading(false);
    }
  };

  const adopt = async () => {
    if (!detail) return;
    setActionLoading(true);
    setError('');
    setValidation(null);
    try {
      const result = await canonicalRequest(fetchWithAuth, `/publications/${detail.id}/adopt`, {
        method: 'POST',
        body: JSON.stringify({
          expected_version: detail.desired_version,
          managed_field_groups: fields,
          copy_observed_values: copyObserved,
        }),
      });
      setValidation(result.validation || null);
      await loadDetail();
      onChanged?.();
    } catch (adoptError) {
      setValidation(adoptError.validation || null);
      setError(adoptError.status === 409 ? 'This publication changed. Reload it before adopting fields.' : adoptError.message || 'Unable to adopt publication');
    } finally {
      setActionLoading(false);
    }
  };

  const previewPlan = async () => {
    if (!detail) return;
    setActionLoading(true);
    setError('');
    setValidation(null);
    try {
      const result = await canonicalRequest(fetchWithAuth, `/publications/${detail.id}/plan`, {
        method: 'POST',
        body: JSON.stringify({ expires_in_seconds: 1800 }),
      });
      setPlan(result);
      setWorkflow(null);
    } catch (planError) {
      setValidation(planError.validation || null);
      setError(planError.message || 'Unable to create publication plan');
    } finally {
      setActionLoading(false);
    }
  };

  const applyPlan = async () => {
    if (!plan?.id || !plan?.plan_hash) return;
    setActionLoading(true);
    setError('');
    setValidation(null);
    try {
      const result = await canonicalRequest(fetchWithAuth, `/publication-plans/${plan.id}/apply`, {
        method: 'POST',
        body: JSON.stringify({ plan_hash: plan.plan_hash }),
      });
      setWorkflow(result);
      onChanged?.();
    } catch (applyError) {
      setValidation(applyError.validation || null);
      setError(applyError.message || 'Unable to start publication workflow');
    } finally {
      setActionLoading(false);
    }
  };

  const openWorkflow = () => {
    if (!workflow?.workflow_run_id) return;
    onClose();
    navigate(`/activity?kind=workflow&q=${workflow.workflow_run_id}`);
  };

  return (
    <Dialog open={open} onClose={actionLoading ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle>Manage {publication?.connection?.display_name || publication?.provider || 'publication'}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ minHeight: 260, display: 'grid', placeItems: 'center' }}><CircularProgress aria-label="Loading publication" /></Box>
        ) : (
          <Stack spacing={2.5}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <ValidationSummary validation={validation} />
            {detail ? (
              <>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}><Typography variant="caption" color="text.secondary">Status</Typography><Typography>{detail.status}</Typography></Paper>
                  <Paper variant="outlined" sx={{ p: 1.5 }}><Typography variant="caption" color="text.secondary">Desired version</Typography><Typography>{detail.desired_version}</Typography></Paper>
                  <Paper variant="outlined" sx={{ p: 1.5 }}><Typography variant="caption" color="text.secondary">Remote ID</Typography><Typography sx={{ overflowWrap: 'anywhere' }}>{detail.external_id || 'Not linked yet'}</Typography></Paper>
                </Box>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle2">Channel-specific settings</Typography>
                      <Typography variant="body2" color="text.secondary">Overrides apply only to this destination and leave the reusable Listing unchanged. Saving is local and does not call the provider.</Typography>
                    </Box>
                    <TextField select label="Publication route" value={routeID} onChange={(event) => setRouteID(event.target.value)} disabled={actionLoading || !connectionDetail}>
                      <MenuItem value=""><em>Direct sales-channel route</em></MenuItem>
                      {routes.map((route) => <MenuItem key={route.id} value={route.id}>{route.name} · {route.strategy}</MenuItem>)}
                    </TextField>
                    <Autocomplete
                      multiple
                      options={collectionOptions}
                      value={collections}
                      onChange={(event, next) => setCollections(next)}
                      getOptionLabel={(option) => option.name || ''}
                      isOptionEqualToValue={(option, selected) => option.id === selected.id}
                      disabled={actionLoading}
                      renderInput={(params) => <TextField {...params} label="Collections" helperText="Destination mappings are preserved and validated when a plan is created." />}
                    />
                    <TextField label="Channel overrides (JSON)" multiline minRows={4} value={overridesText} onChange={(event) => setOverridesText(event.target.value)} disabled={actionLoading} helperText="Use only values that should differ from the base Listing for this destination." />
                    <Box><Button variant="outlined" onClick={saveSettings} disabled={actionLoading}>Save channel settings</Button></Box>
                  </Stack>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2">Adopt and manage fields</Typography>
                  <Typography variant="body2" color="text.secondary">This changes local ownership only. It does not call the provider.</Typography>
                  <ManagedFieldSelector value={fields} onChange={setFields} connection={detail.connection} disabled={actionLoading} />
                  <FormControlLabel control={<Checkbox checked={copyObserved} onChange={(event) => setCopyObserved(event.target.checked)} />} label="Copy normalized remote content into this Listing when adopting content" />
                  <Box sx={{ mt: 1 }}><Button variant="outlined" onClick={adopt} disabled={actionLoading || fields.length === 0}>Adopt selected fields</Button></Box>
                </Paper>
                {flags.publication_workflows ? (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} alignItems={{ sm: 'center' }}>
                      <Box><Typography variant="subtitle2">Publication plan</Typography><Typography variant="body2" color="text.secondary">Preview exact intended effects. Planning cannot mutate a provider.</Typography></Box>
                      <Button variant="contained" onClick={previewPlan} disabled={actionLoading}>{actionLoading ? 'Planning…' : 'Preview plan'}</Button>
                    </Stack>
                    {plan ? (
                      <Stack spacing={1.5} sx={{ mt: 2 }}>
                        <Alert severity="info">
                          Immutable plan {plan.reused ? 'reused' : 'created'} with hash {plan.plan_hash?.slice(0, 12)}…. Nothing changes remotely until you start it.
                        </Alert>
                        {(plan.warnings || []).map((warning) => (
                          <Alert key={`${warning.code}-${warning.field}`} severity="warning">{warning.message}</Alert>
                        ))}
                        <Stack spacing={1}>
                          {(plan.plan?.actions || []).map((action, index) => (
                            <Paper key={action.key} variant="outlined" sx={{ p: 1.5 }}>
                              <Stack direction="row" spacing={1.25} alignItems="flex-start">
                                <Chip size="small" label={index + 1} sx={{ mt: 0.1 }} />
                                <Box>
                                  <Typography variant="body2">{action.description}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {action.field_group ? `${action.field_group} · ` : ''}{action.type}
                                  </Typography>
                                </Box>
                              </Stack>
                            </Paper>
                          ))}
                        </Stack>
                        {workflow ? (
                          <Alert severity="success" action={<Button color="inherit" size="small" onClick={openWorkflow}>Open Activity</Button>}>
                            Workflow {workflow.reused ? 'already exists' : 'started'} and is {workflow.status}. PODBoss will resume it from durable checkpoints after retries or restarts.
                          </Alert>
                        ) : (
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                              Provider writes are throttled, retried when safe, and followed by remote verification.
                            </Typography>
                            <Button variant="contained" onClick={applyPlan} disabled={actionLoading}>
                              {actionLoading ? 'Starting…' : 'Start publication'}
                            </Button>
                          </Stack>
                        )}
                        <Paper variant="outlined" sx={{ p: 1, maxHeight: 320, overflow: 'auto' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1, pt: 0.5 }}>Advanced plan details</Typography>
                          <JsonTreeViewer data={plan.plan} />
                        </Paper>
                      </Stack>
                    ) : null}
                  </Paper>
                ) : null}
              </>
            ) : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={actionLoading}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
